import { useEffect, useState } from "react";
import { FiMaximize2 } from "react-icons/fi";
import HistoryCard from "./GenerationPage/HistoryCard";
import HistoryCompareViewer from "./HistoryCompareViewer";
import type { HistoryItem } from "../../types/smileDesign/historyItem";

interface Props {
    history: HistoryItem[];
    selectedImage: HistoryItem | null;
    setSelectedImage: (img: HistoryItem) => void;
    patientName: string;
    handleDownload: (url: string, filename: string) => void;
}

export default function HistorySection({
    history,
    selectedImage,
    setSelectedImage,
    patientName,
    handleDownload,
}: Props) {
    const [compareSelecting, setCompareSelecting] = useState(false);
    const [compareIndices, setCompareIndices] = useState<number[]>([]);
    const [showCompareViewer, setShowCompareViewer] = useState(false);

    const canCompare = history.length >= 2;

    const resetCompare = () => {
        setCompareSelecting(false);
        setCompareIndices([]);
    };

    useEffect(() => {
        if (compareSelecting && compareIndices.length === 2) {
            setShowCompareViewer(true);
        }
    }, [compareSelecting, compareIndices]);

    const openCompareFullscreen = () => {
        if (!canCompare) return;

        if (compareIndices.length === 2) {
            setShowCompareViewer(true);
            return;
        }

        setCompareSelecting(true);
        setCompareIndices([]);
    };

    const handleCardSelect = (item: HistoryItem, index: number) => {
        if (!compareSelecting) {
            setSelectedImage(item);
            return;
        }

        setCompareIndices((prev) => {
            if (prev.includes(index)) {
                return prev.filter((i) => i !== index);
            }
            if (prev.length >= 2) {
                return [prev[1], index];
            }
            return [...prev, index];
        });
    };

    const compareSlot = (index: number): "A" | "B" | null => {
        const slot = compareIndices.indexOf(index);
        if (slot === 0) return "A";
        if (slot === 1) return "B";
        return null;
    };

    return (
        <>
            <div className="bg-[#212226] border border-white/[0.07] rounded-[14px] p-4 sm:p-5">
                <div className="flex items-center justify-between gap-3 mb-3.5">
                    <div className="min-w-0">
                        <h3 className="text-sm font-semibold">Design History</h3>
                        {history.length > 0 && (
                            <p className="text-sm text-[#6B6C72] mt-0.5">
                                {history.length} generated design
                                {history.length !== 1 ? "s" : ""}
                                {compareSelecting
                                    ? ` · Select ${Math.max(0, 2 - compareIndices.length)} more to compare`
                                    : ""}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {compareSelecting && (
                            <button
                                type="button"
                                onClick={resetCompare}
                                className="px-2.5 sm:px-3 py-2 rounded-[9px] text-sm font-semibold border border-white/[0.07] text-[#A5A6AC] hover:text-white hover:border-white/[0.14] transition-colors"
                            >
                                Cancel
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={openCompareFullscreen}
                            disabled={!canCompare}
                            title={
                                canCompare
                                    ? compareIndices.length === 2
                                        ? "Open fullscreen compare"
                                        : "Select 2 after images to compare"
                                    : "Need at least 2 designs to compare"
                            }
                            className="flex items-center gap-1.5 bg-[rgba(29,168,198,0.14)] text-[#1DA8C6] px-2.5 sm:px-3 py-2 rounded-[9px] text-sm font-semibold hover:bg-[rgba(29,168,198,0.24)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <FiMaximize2 className="w-[13px] h-[13px]" />
                            <span className="hidden sm:inline">
                                {compareIndices.length === 2 ? "Fullscreen" : "Compare"}
                            </span>
                        </button>
                    </div>
                </div>

                {compareSelecting && (
                    <div className="mb-3.5 px-3 py-2.5 rounded-[9px] bg-[rgba(29,168,198,0.08)] border border-[rgba(29,168,198,0.2)] text-sm text-[#A5A6AC]">
                        Tap two after designs to open a side-by-side fullscreen compare.
                        Each side will have its own prev/next controls.
                    </div>
                )}

                {history.length === 0 ? (
                    <div className="flex items-center justify-center rounded-[9px] border border-dashed border-white/[0.07] bg-[#26282C]/50 py-12 text-center">
                        <div>
                            <p className="text-sm font-medium text-[#A5A6AC]">No history yet</p>
                            <p className="text-sm text-[#6B6C72] mt-1">
                                Generated smiles will appear here
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
                        {history.map((item, index) => {
                            const version =
                                history
                                    .slice(0, index + 1)
                                    .filter((h) => h.smileType === item.smileType).length;

                            return (
                                <HistoryCard
                                    key={`${item.image}-${index}`}
                                    item={item}
                                    index={index}
                                    version={version}
                                    patientName={patientName}
                                    isSelected={
                                        compareSelecting
                                            ? compareIndices.includes(index)
                                            : selectedImage?.image === item.image
                                    }
                                    compareSlot={compareSelecting ? compareSlot(index) : null}
                                    onSelect={() => handleCardSelect(item, index)}
                                    onDownload={handleDownload}
                                />
                            );
                        })}
                    </div>
                )}
            </div>

            {showCompareViewer && compareIndices.length === 2 && (
                <HistoryCompareViewer
                    history={history}
                    leftIndex={compareIndices[0]}
                    rightIndex={compareIndices[1]}
                    onClose={() => {
                        setShowCompareViewer(false);
                        resetCompare();
                    }}
                />
            )}
        </>
    );
}
